import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '../client.ts';

/**
 * Generic CRUD hooks for simple single-table resources. Mirrors the
 * server's pattern from /server/src/lib/crud.ts.
 *
 *   useList(resource)        → GET /api/{resource}
 *   useOne(resource, id)     → GET /api/{resource}/{id}
 *   useCreate(resource)      → POST /api/{resource}
 *   useUpdate(resource)      → PUT /api/{resource}  (with id on body)
 *   useRemove(resource)      → DELETE /api/{resource}/{id}
 *
 * Each invalidates the list query so the table refetches after writes.
 */
export function listKey(resource: string) {
  return ['crud', resource, 'list'] as const;
}
export function oneKey(resource: string, id: number | string) {
  return ['crud', resource, 'one', id] as const;
}

export function useList<T>(resource: string) {
  return useQuery({
    queryKey: listKey(resource),
    queryFn: () => http.get<T[]>(`/api/${resource}`),
  });
}

export function useOne<T>(resource: string, id: number | string | null | undefined) {
  return useQuery({
    queryKey: oneKey(resource, id ?? '___none'),
    queryFn: () => http.get<T>(`/api/${resource}/${id}`),
    enabled: id != null,
  });
}

export function useCreate<T>(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<T>) => http.post<T>(`/api/${resource}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(resource) }),
  });
}

export function useUpdate<T extends { id?: number | null }>(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: T) => http.put<T>(`/api/${resource}`, data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: listKey(resource) });
      if (data.id != null) qc.invalidateQueries({ queryKey: oneKey(resource, data.id) });
    },
  });
}

export function useRemove(resource: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete(`/api/${resource}/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(resource) }),
  });
}
